import { Button } from '@finn/design-system/atoms/button';
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
} from '@finn/design-system/atoms/modal';
import { Textarea } from '@finn/design-system/atoms/textarea';
import { ModalKey, useCloseModal, useIsModalOpen } from '@finn/ua-modals';
import { TrackingEventName, useTrackingStore } from '@finn/ua-tracking';
import { useIntl, useSession } from '@finn/ui-utils';
import { useCallback, useEffect, useState } from 'react';

import { useProductPreference } from '~/hooks';

export function ProductPreferenceDialog() {
  const intl = useIntl();
  const track = useTrackingStore((state) => state.track);
  const [session] = useSession();
  const isModalOpen = useIsModalOpen(ModalKey.PRODUCT_PREFERENCE_MODAL);
  const closeModal = useCloseModal();

  const [comment, setComment] = useState('');
  const { submitPreference, isLoading, isSuccess, error, reset } =
    useProductPreference();

  const handleClose = useCallback(() => {
    closeModal(ModalKey.PRODUCT_PREFERENCE_MODAL);
    reset();
    setComment('');
  }, [closeModal, reset]);

  useEffect(() => {
    if (isModalOpen) {
      track(TrackingEventName.MODAL_OPENED, {
        name: 'Retention Preference Modal',
        location: 'PLP',
      });
    }
  }, [isModalOpen, track]);

  const handleSubmitProductInterest = async () => {
    if (!comment.trim()) return;

    const contactId = session?.user?.hs_contact_id;
    if (!contactId) {
      console.error('No contact ID found in session');

      return;
    }

    try {
      const success = await submitPreference({
        contact_id: contactId,
        preference: comment.trim(),
      });

      if (success) {
        track(TrackingEventName.CTA_CLICKED, {
          location: 'PLP',
        });
      }
    } catch (submitError) {
      console.error('Error submitting product preferences:', submitError);
    }
  };

  if (isSuccess) {
    return (
      <Modal variant="small" open={isModalOpen} onOpenChange={handleClose}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {intl.formatMessage(
                'general.productPreferenceModal.successTitle'
              )}
            </ModalTitle>
            <ModalSubtitle>
              {intl.formatMessage(
                'general.productPreferenceModal.successSubtitle'
              )}
            </ModalSubtitle>
          </ModalHeader>
          <ModalFooter variant="small" className="mt-8">
            <Button onClick={handleClose}>
              {intl.formatMessage('general.productPreferenceModal.successCta')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal variant="small" open={isModalOpen} onOpenChange={handleClose}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>
            {intl.formatMessage('general.productPreferenceModal.title')}
          </ModalTitle>
          <ModalSubtitle>
            {intl.formatMessage('general.productPreferenceModal.subtitle')}
          </ModalSubtitle>
        </ModalHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleSubmitProductInterest();
          }}
        >
          <Textarea
            value={comment}
            onChange={(e) => {
              setComment(e.target.value);
            }}
            placeholder={intl.formatMessage(
              'general.productPreferenceModal.placeholder'
            )}
            rows={3}
            error={
              error
                ? intl.formatMessage('general.productPreferenceModal.error')
                : undefined
            }
          />
          <p className="mt-2 body-12-regular">
            {intl.formatMessage('general.productPreferenceModal.hint')}
          </p>
          <ModalFooter variant="small" className="mt-8">
            <Button
              type="submit"
              loading={isLoading}
              disabled={!comment.trim()}
            >
              {intl.formatMessage('general.productPreferenceModal.submit')}
            </Button>
            <Button onClick={handleClose} variant="ghost">
              {intl.formatMessage('general.productPreferenceModal.cancel')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
